import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { pvSharingService, propositionsService } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const SharedPVView = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [pv, setPv] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Modal de proposition
  const [showPropositionModal, setShowPropositionModal] = useState(false);
  const [propositionForm, setPropositionForm] = useState({
    nom_membre: '',
    titre: '',
    description: ''
  });
  const [isSubmittingProposition, setIsSubmittingProposition] = useState(false);
  const [propositionError, setPropositionError] = useState(null);
  const [propositionSuccess, setPropositionSuccess] = useState(false);
  const [members, setMembers] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setError(null);

    try {
      const response = await pvSharingService.verifySharedPV(shareToken, password);
      setPv(response.pv);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Mot de passe incorrect ou lien invalide');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRefreshPV = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const response = await pvSharingService.getSharedPV(shareToken);
      setPv(response.pv);
    } catch (err) {
      console.error('Error refreshing PV:', err);
      setError('Erreur lors du rafraîchissement du PV');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleOpenPropositionModal = async () => {
    setShowPropositionModal(true);
    setPropositionError(null);
    setPropositionSuccess(false);
    setPropositionForm({
      nom_membre: '',
      titre: '',
      description: ''
    });
    
    // Load members
    try {
      const response = await pvSharingService.getMembersForSharedPV(shareToken);
      setMembers(response.members || []);
    } catch (err) {
      console.error('Error loading members:', err);
      setMembers([]);
    }
  };

  const handleClosePropositionModal = () => {
    setShowPropositionModal(false);
    setPropositionForm({
      nom_membre: '',
      titre: '',
      description: ''
    });
    setPropositionError(null);
    setPropositionSuccess(false);
  };

  const handlePropositionChange = (e) => {
    const { name, value } = e.target;
    setPropositionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitProposition = async (e) => {
    e.preventDefault();
    setIsSubmittingProposition(true);
    setPropositionError(null);

    try {
      // Soumettre la proposition directement au PV partagé
      await pvSharingService.addPropositionToSharedPV(shareToken, {
        nom_membre: propositionForm.nom_membre,
        titre: propositionForm.titre,
        description: propositionForm.description
      });

      setPropositionSuccess(true);
      
      // Fermer le modal après 2 secondes
      setTimeout(() => {
        handleClosePropositionModal();
      }, 2000);

    } catch (err) {
      console.error('Error submitting proposition:', err);
      setPropositionError(err.response?.data?.detail || 'Erreur lors de la soumission de la proposition');
    } finally {
      setIsSubmittingProposition(false);
    }
  };

  if (isAuthenticated && pv) {
    return (
      <>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Header with Read-Only Badge */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Eye size={24} className="text-emerald-600" />
                PV Partagé
              </h1>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleRefreshPV}
                  disabled={isRefreshing}
                  variant="outline"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  title="Actualiser le PV"
                >
                  <RefreshCw size={18} className={`${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  onClick={handleOpenPropositionModal}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Plus size={18} className="mr-2" />
                  Ajouter un point au PV
                </Button>
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-amber-50 border border-amber-200 px-4 py-2 rounded-full">
                  <Lock size={16} />
                  <span className="font-medium">Mode lecture seule</span>
                </div>
              </div>
            </div>
          </div>

          {/* PV Content Card */}
          <Card className="overflow-hidden shadow-lg">
            <CardContent className="p-8">
              {/* En-tête du PV */}
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                  Tennis Club Cheyres-Châbles
                </h2>
                <p className="text-xl text-gray-600">
                  Procès-verbal du comité
                </p>
              </div>

              {/* Informations générales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto mb-8">
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">📅</span>
                  <span className="font-medium">Date :</span>
                  <span>{formatDate(pv.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-600">🕐</span>
                  <span className="font-medium">Heure :</span>
                  <span>{pv.time}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600">👥</span>
                  <span>
                    <span className="font-medium">Présents :</span> {Array.isArray(pv.presents) ? pv.presents.join(', ') : pv.presents || 'Aucun'}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-600">🚫</span>
                  <span>
                    <span className="font-medium">Excusés :</span> {Array.isArray(pv.excuses) ? pv.excuses.join(', ') : pv.excuses || 'Aucun'}
                  </span>
                </div>
              </div>

              <hr className="border-gray-300 mb-8" />

              {/* Contenu du PV */}
              <div className="space-y-8">
                {/* 1. Approbation du dernier PV */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-green-600">✅</span>
                    1. Approbation du dernier PV
                  </h3>
                  <p className="text-gray-700 ml-6 leading-relaxed whitespace-pre-wrap">
                    {pv.approbation_dernier_pv || 'Aucune information renseignée.'}
                  </p>
                </div>

                {/* 2. Suivi des décisions précédentes */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-blue-600">📋</span>
                    2. Suivi des décisions précédentes
                  </h3>
                  <p className="text-gray-700 ml-6 leading-relaxed whitespace-pre-wrap">
                    {pv.suivi_decisions || 'Aucune information renseignée.'}
                  </p>
                </div>

                {/* 3. Points à l'ordre du jour */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-purple-600">📝</span>
                    3. Points à l'ordre du jour
                  </h3>
                  <div className="ml-6">
                    {pv.points_ordre_jour && pv.points_ordre_jour.length > 0 ? (
                      <ol className="space-y-3">
                        {pv.points_ordre_jour.map((point, index) => {
                          const notes = point.notes || [];
                          return (
                          <li key={index} className="text-gray-700 leading-relaxed">
                            <div className="flex items-start gap-2">
                              <span>{index + 1}. {point.titre}</span>
                              {point.traite && (
                                <span className="text-green-600 text-base font-bold ml-1">&#10003;</span>
                              )}
                            </div>
                            {notes.length > 0 && (
                              <div className="mt-2 ml-4 space-y-2">
                                {notes.map((note, noteIdx) => (
                                  <div key={noteIdx} className={`border-l-2 pl-3 ${note.termine ? 'border-green-300' : 'border-blue-200'}`}>
                                    <span className={`text-xs font-semibold ${note.termine ? 'text-green-600' : 'text-blue-600'}`}>
                                      {note.date}
                                      {note.termine && <span className="ml-1 bg-green-100 text-green-700 px-1 py-0.5 rounded text-[10px]">Terminé</span>}
                                    </span>
                                    <p className={`text-sm whitespace-pre-wrap ${note.termine ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{note.texte}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(!notes || notes.length === 0) && point.remarques && point.remarques.trim() && (
                              <div className="mt-2 ml-4 space-y-2">
                                {point.remarques.split('\n').reduce((acc, line) => {
                                  const dateMatch = line.match(/^\[(.+?)\]\s*[-:]?\s*(.*)/);
                                  if (dateMatch) {
                                    acc.push({ date: dateMatch[1], texte: dateMatch[2] || '' });
                                  } else if (acc.length > 0) {
                                    acc[acc.length - 1].texte += '\n' + line;
                                  } else {
                                    acc.push({ date: '', texte: line });
                                  }
                                  return acc;
                                }, []).map((note, nIdx) => (
                                  <div key={nIdx} className="border-l-2 border-blue-200 pl-3">
                                    {note.date && <span className="text-xs font-semibold text-blue-600 block">{note.date}</span>}
                                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{note.texte}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </li>
                          );
                        })}
                      </ol>
                    ) : (
                      <p className="text-gray-700">Aucun point renseigné.</p>
                    )}
                  </div>
                </div>

                {/* 4. Divers / Propositions individuelles */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    4. Divers / Propositions individuelles
                  </h3>
                  <div className="ml-6 space-y-3">
                    {/* Propositions individuelles structurées */}
                    {(() => {
                      const propositionsIndividuelles = pv.propositions_individuelles || pv.propositionsIndividuelles;
                      const diversPropositions = pv.divers_propositions || pv.diversPropositions;
                      
                      return (
                        <>
                          {propositionsIndividuelles && propositionsIndividuelles.length > 0 && (
                            <div className="space-y-2">
                              {propositionsIndividuelles.map((proposition, index) => (
                                <div key={index} className={`p-3 rounded-lg ${
                                  proposition.statut === 'validé' ? 'bg-green-50 border border-green-200' :
                                  proposition.statut === 'refusé' ? 'bg-red-50 border border-red-200' :
                                  'bg-gray-50'
                                }`}>
                                  <div className="flex items-start gap-2">
                                    {/* Indicateur de statut */}
                                    <div className="flex-shrink-0 mt-0.5">
                                      {proposition.statut === 'validé' && (
                                        <span className="text-green-600 font-bold text-lg" title="Proposition validée">✓</span>
                                      )}
                                      {proposition.statut === 'refusé' && (
                                        <span className="text-red-600 font-bold text-lg" title="Proposition refusée">✗</span>
                                      )}
                                      {!proposition.statut && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                          En discussion
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <span className="font-semibold text-gray-700">{proposition.membre}</span>
                                      <span className="text-gray-600"> propose : </span>
                                      <span className="text-gray-700">{proposition.contenu}</span>
                                      
                                      {/* Remarques sur la décision */}
                                      {proposition.remarques && proposition.statut === 'refusé' && (
                                        <div className="mt-2 p-2 bg-red-100 rounded text-sm">
                                          <span className="font-medium text-red-800">Remarques :</span>
                                          <span className="text-red-700 ml-1">{proposition.remarques}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Points divers généraux */}
                          {diversPropositions && (
                            <div className={propositionsIndividuelles && propositionsIndividuelles.length > 0 ? "mt-4" : ""}>
                              {propositionsIndividuelles && propositionsIndividuelles.length > 0 && (
                                <h4 className="font-medium text-gray-700 mb-2">Autres points divers :</h4>
                              )}
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <div className="whitespace-pre-wrap text-gray-700">{diversPropositions}</div>
                              </div>
                            </div>
                          )}
                          
                          {/* Message si aucune proposition */}
                          {(!propositionsIndividuelles || propositionsIndividuelles.length === 0) && !diversPropositions && (
                            <p className="text-gray-700">Aucune proposition renseignée.</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* 5. Prochaines étapes et tâches attribuées */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-indigo-600">📋</span>
                    5. Prochaines étapes et tâches attribuées
                  </h3>
                  <div className="ml-6">
                    {pv.prochaines_etapes && pv.prochaines_etapes.filter(etape => etape.tache && etape.tache.trim()).length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="border border-gray-300 px-4 py-2 text-left font-medium w-2/5">
                                Tâche
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-left font-medium w-1/5">
                                Responsable
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-left font-medium w-1/5">
                                Délai
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-left font-medium w-1/5">
                                Statut
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Trier les tâches par statut : En cours, En suspens, Terminé
                              const sortedTasks = [...pv.prochaines_etapes]
                                .filter(etape => etape.tache && etape.tache.trim())
                                .sort((a, b) => {
                                  const statusOrder = { 'En cours': 1, 'En suspens': 2, 'Terminé': 3 };
                                  return (statusOrder[a.statut] || 1) - (statusOrder[b.statut] || 1);
                                });
                              
                              return sortedTasks.map((etape, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-300 px-4 py-2 break-words text-gray-700">{etape.tache}</td>
                                  <td className="border border-gray-300 px-4 py-2 whitespace-nowrap text-gray-700">
                                    {etape.responsable || 'Non assigné'}
                                  </td>
                                  <td className="border border-gray-300 px-4 py-2 whitespace-nowrap text-gray-700">
                                    {etape.delai ? formatDate(etape.delai) : 'Non défini'}
                                  </td>
                                  <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      etape.statut === 'Terminé' 
                                        ? 'bg-green-100 text-green-700' 
                                        : etape.statut === 'En suspens'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {etape.statut || 'En cours'}
                                    </span>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-700">Aucune tâche renseignée.</p>
                    )}
                  </div>
                </div>

                {/* 6. Date du prochain comité */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-teal-600">📅</span>
                    6. Date du prochain comité
                  </h3>
                  <p className="text-gray-700 ml-6 leading-relaxed">
                    {pv.prochain_comite ? formatDate(pv.prochain_comite) : 'Date non définie.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-4 py-3 inline-block">
              <Lock size={14} className="inline mr-2" />
              Ce document est partagé en lecture seule. Vous ne pouvez pas le modifier.
            </p>
          </div>
        </div>

        {/* Modal de proposition */}
        {showPropositionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Ajouter un point au PV</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Proposez un point à ajouter à l'ordre du jour
                  </p>
                </div>
                <button
                  onClick={handleClosePropositionModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isSubmittingProposition}
                >
                  <AlertCircle size={24} />
                </button>
              </div>

              {/* Content */}
              <form onSubmit={handleSubmitProposition} className="px-6 py-6 space-y-6">
                {/* Success Message */}
                {propositionSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium">
                      ✓ Votre proposition a été soumise avec succès !
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Elle sera examinée par le comité lors de la prochaine réunion.
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {propositionError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{propositionError}</p>
                  </div>
                )}

                {/* Info Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>📝 Comment ça marche ?</strong>
                  </p>
                  <ul className="text-xs text-blue-700 mt-2 space-y-1">
                    <li>• Remplissez le formulaire ci-dessous</li>
                    <li>• Votre proposition sera soumise au comité</li>
                    <li>• Elle pourra être validée, refusée ou mise en attente</li>
                  </ul>
                </div>

                {/* Nom - Liste déroulante */}
                <div>
                  <label htmlFor="nom_membre" className="block text-sm font-medium text-gray-700 mb-2">
                    Votre nom <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="nom_membre"
                    name="nom_membre"
                    value={propositionForm.nom_membre}
                    onChange={handlePropositionChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    required
                    disabled={isSubmittingProposition || propositionSuccess}
                  >
                    <option value="">Sélectionnez votre nom</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.name}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>

 

                {/* Titre */}
                <div>
                  <label htmlFor="titre" className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet / Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="titre"
                    name="titre"
                    value={propositionForm.titre}
                    onChange={handlePropositionChange}
                    placeholder="Ex: Organisation tournoi d'été"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    disabled={isSubmittingProposition || propositionSuccess}
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optionnel)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={propositionForm.description}
                    onChange={handlePropositionChange}
                    placeholder="Décrivez votre proposition en détail..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    disabled={isSubmittingProposition || propositionSuccess}
                  />
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t -mx-6 -mb-6 px-6 py-4 flex justify-end gap-3">
                  <Button
                    type="button"
                    onClick={handleClosePropositionModal}
                    variant="outline"
                    disabled={isSubmittingProposition}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmittingProposition || propositionSuccess}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {isSubmittingProposition ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      'Envoyer la proposition'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      </>
    );
  }

  // Password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600 rounded-full mb-4">
            <Lock size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            PV Partagé
          </h1>
          <p className="text-gray-600">
            Ce procès-verbal est protégé par mot de passe
          </p>
        </div>

        {/* Password Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>🔒 Accès sécurisé</strong>
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Saisissez le mot de passe qui vous a été communiqué pour accéder au document.
              </p>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
                autoFocus
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isVerifying || !password}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Vérification...
                </span>
              ) : (
                'Accéder au PV'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Vous n'avez pas le mot de passe ? Contactez l'administrateur du club.
        </p>
      </div>
    </div>
  );
};

export default SharedPVView;
